/**
 * Password Reset Request Limiter
 * Manages password reset request rate limiting using localStorage
 * Rate limit: 1 hour between requests (when rate limited by backend)
 * This is separate from OTP resend limiter
 */

import logger from './logger'

const PASSWORD_RESET_LIMIT_STORAGE_KEY = 'password_reset_limiter'
const RATE_LIMIT_HOURS = 1 // 1 hour as per API documentation

/**
 * Check if password reset can be requested for the given email
 * @param {string} email - email address
 * @returns {Object} { canRequest, limitEndTime }
 */
export const checkPasswordResetLimit = email => {
    let stored = {}
    try {
        const storedData = localStorage.getItem(PASSWORD_RESET_LIMIT_STORAGE_KEY)
        if (storedData) {
            stored = JSON.parse(storedData)
        }
    } catch (error) {
        logger.warn('Failed to read password reset limit from localStorage:', error)
        return {
            canRequest: true,
            limitEndTime: null,
        }
    }
    const now = Date.now()

    // Different email, reset
    if (stored.email !== email) {
        return {
            canRequest: true,
            limitEndTime: null,
        }
    }

    // Check if rate limit is still active
    if (stored.limitEndTime && now < stored.limitEndTime) {
        return {
            canRequest: false,
            limitEndTime: stored.limitEndTime,
        }
    }

    return {
        canRequest: true,
        limitEndTime: null,
    }
}

/**
 * Record a rate limit for the given email (when backend returns rate limit error)
 * @param {string} email - email address
 * @param {string|number|Date|null} customLimitEndTime - custom limit end time from backend (optional)
 * @returns {Object} updated limit data with limitEndTime
 */
export const recordPasswordResetRateLimit = (email, customLimitEndTime = null) => {
    const now = Date.now()
    let limitEndTime

    if (customLimitEndTime) {
        // Convert to timestamp if it's a Date object or ISO string
        if (typeof customLimitEndTime === 'number') {
            limitEndTime = customLimitEndTime
        } else if (customLimitEndTime instanceof Date) {
            limitEndTime = customLimitEndTime.getTime()
        } else {
            // Assume ISO string
            limitEndTime = new Date(customLimitEndTime).getTime()
        }
    } else {
        // Default: 1 hour from now
        limitEndTime = now + RATE_LIMIT_HOURS * 60 * 60 * 1000
    }

    const newData = {
        email,
        limitEndTime,
    }
    try {
        localStorage.setItem(PASSWORD_RESET_LIMIT_STORAGE_KEY, JSON.stringify(newData))
    } catch (error) {
        logger.warn('Failed to save password reset limit to localStorage:', error)
    }
    return newData
}

/**
 * Clear rate limit data (for testing or manual reset)
 * @param {string} email - optional email to clear specific email data
 */
export const clearPasswordResetLimit = email => {
    try {
        if (email) {
            const storedData = localStorage.getItem(PASSWORD_RESET_LIMIT_STORAGE_KEY)
            if (storedData) {
                const stored = JSON.parse(storedData)
                if (stored.email === email) {
                    localStorage.removeItem(PASSWORD_RESET_LIMIT_STORAGE_KEY)
                }
            }
        } else {
            localStorage.removeItem(PASSWORD_RESET_LIMIT_STORAGE_KEY)
        }
    } catch (error) {
        logger.warn('Failed to clear password reset limit from localStorage:', error)
    }
}

/**
 * Calculate time left until rate limit expires
 * @param {Object} limit - Limit object from checkPasswordResetLimit
 * @returns {number} Time left in seconds (0 if no limit)
 */
export const calculatePasswordResetTimeLeft = limit => {
    if (!limit.limitEndTime) {
        return 0
    }
    const now = Date.now()
    const timeLeft = Math.max(0, Math.floor((limit.limitEndTime - now) / 1000))
    return timeLeft
}

/**
 * Format time left as MM:SS or HH:MM:SS
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string
 */
export const formatPasswordResetTimeLeft = seconds => {
    if (seconds <= 0) return '00:00'

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    }
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}
